import React, { useMemo } from 'react';
import { Badge, Card, Divider, ScrollArea, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import KpiPage, {
  ChartCard,
  ChartTooltip,
  QueryState,
  StatTile,
  fmtInt,
  fmtMoney,
  fmtPct,
  useChartColors,
} from '../KpiPage';
import { kpiBySlug } from '../kpiCatalog';
import { useWindowParams } from '../DateWindowSelector';
import useNexusQuery, { field, num } from '../../../hooks/useNexusQuery';

const kpi = kpiBySlug('unmanaged-parts-sales');

function SectionHeading({ title, subtitle }) {
  return (
    <Stack gap={4} mt="lg">
      <Text fw={800} size="xl">
        {title}
      </Text>
      {subtitle && (
        <Text size="sm" c="dimmed">
          {subtitle}
        </Text>
      )}
      <Divider mt={2} />
    </Stack>
  );
}

export default function UnmanagedPartsSales() {
  const c = useChartColors();
  const [window, setWindow] = useWindowParams();

  const summary = useNexusQuery('initiative-unmanaged-vs-managed-summary', window);
  const condition = useNexusQuery('initiative-unmanaged-by-condition', window);
  const channel = useNexusQuery('initiative-unmanaged-by-channel', window);
  const candidatesQ = useNexusQuery('initiative-unmanaged-promotion-candidates', window);

  // --- Section 1: size, as a share of all sales ---
  const size = useMemo(() => {
    const r = summary.rows[0];
    const g = (k) => (r ? num(r, k) : 0);
    const share = (a, b) => (g(b) ? (100 * g(a)) / g(b) : 0);
    return {
      revenue: g('UnmanagedRevenue'),
      revenuePct: share('UnmanagedRevenue', 'AllRevenue'),
      lines: g('UnmanagedLines'),
      linePct: share('UnmanagedLines', 'AllLines'),
      parts: g('UnmanagedDistinctParts'),
      partsPct: share('UnmanagedDistinctParts', 'AllDistinctParts'),
    };
  }, [summary.rows]);

  // --- condition breakdown (revenue) ---
  const condColor = (cond) =>
    ({ AR: c.series[3], SV: c.positive, OH: c.series[4], NEW: c.series[1] }[cond] ?? c.neutral);

  const condData = useMemo(
    () =>
      condition.rows.map((r) => ({
        cond: field(r, 'Condition'),
        revenue: num(r, 'Revenue'),
        qty: num(r, 'Qty'),
      })),
    [condition.rows]
  );

  const arPct = useMemo(() => {
    const total = condData.reduce((s, d) => s + d.revenue, 0);
    const ar = condData.find((d) => d.cond === 'AR')?.revenue ?? 0;
    return total ? (100 * ar) / total : 0;
  }, [condData]);

  // --- Section 2: commercial pathways (channel) ---
  const channelColor = (ch) =>
    ({
      'End User / Operator': c.series[0],
      Reseller: c.series[2],
      'Repair Station': c.series[4],
      'OEM / Tier 1': c.series[1],
    }[ch] ?? c.neutral);

  const byChannel = useMemo(() => {
    const map = {};
    channel.rows.forEach((r) => {
      const ch = field(r, 'Channel');
      map[ch] = (map[ch] || 0) + num(r, 'Revenue');
    });
    return Object.entries(map)
      .map(([ch, revenue]) => ({ channel: ch, revenue }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [channel.rows]);

  // --- Section 3: promotion candidates ---
  const candidates = useMemo(
    () =>
      candidatesQ.rows.map((r) => ({
        part: field(r, 'Partnumber'),
        desc: field(r, 'Description'),
        cls: field(r, 'Class'),
        months: num(r, 'MonthsSold'),
        customers: num(r, 'Customers'),
        revenue: num(r, 'Revenue'),
        marginPct: num(r, 'GrossMarginPct'),
      })),
    [candidatesQ.rows]
  );

  return (
    <KpiPage kpi={kpi} window={window} onWindowChange={setWindow} hideMethodology>
      <Stack gap="md">
        {/* ============================ SECTION 1 — UNMANAGED PART SALES ======================== */}
        <SectionHeading
          title="1 · Unmanaged part sales"
          subtitle="Non-managed material is not ignored — it's actively commercialized. How big is it as a share of the business, and how is it sold?"
        />

        <QueryState isLoading={summary.isLoading} error={summary.error} isEmpty={!summary.rows.length}>
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            <StatTile
              label="Unmanaged revenue"
              value={fmtMoney(size.revenue)}
              sub={`${fmtPct(size.revenuePct)} of all sales revenue`}
              color={c.series[0]}
            />
            <StatTile
              label="Unmanaged parts sold"
              value={fmtPct(size.partsPct)}
              sub={`${fmtInt(size.parts)} distinct part numbers`}
              color={c.series[0]}
            />
            <StatTile
              label="Unmanaged sale lines"
              value={fmtPct(size.linePct)}
              sub={`${fmtInt(size.lines)} invoiced lines`}
              color={c.series[0]}
            />
            <StatTile
              label="Sold as-removed (AR)"
              value={fmtPct(arPct)}
              sub="Share of unmanaged revenue — core to channels"
              color={c.series[3]}
            />
          </SimpleGrid>
        </QueryState>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <ChartCard
            title="How unmanaged material is sold — by revenue"
            caption="Revenue by condition. Fairly balanced — repaired SV/OH carry big dollars alongside AR core. This is the disposition, not a pricing judgement."
            height={260}
          >
            <QueryState isLoading={condition.isLoading} error={condition.error} isEmpty={!condData.length}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={condData}
                    dataKey="revenue"
                    nameKey="cond"
                    innerRadius="50%"
                    outerRadius="78%"
                    paddingAngle={2}
                  >
                    {condData.map((d) => (
                      <Cell key={d.cond} fill={condColor(d.cond)} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip formatter={fmtMoney} />} wrapperStyle={{ outline: 'none' }} />
                  <Legend verticalAlign="bottom" iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>

          <ChartCard
            title="How unmanaged material is sold — by quantity"
            caption="The same split by units, and it flips: AR core dominates the count while carrying far less revenue — cheap material moved in volume — versus fewer, high-value repaired (SV/OH) units."
            height={260}
          >
            <QueryState isLoading={condition.isLoading} error={condition.error} isEmpty={!condData.length}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={condData}
                    dataKey="qty"
                    nameKey="cond"
                    innerRadius="50%"
                    outerRadius="78%"
                    paddingAngle={2}
                  >
                    {condData.map((d) => (
                      <Cell key={d.cond} fill={condColor(d.cond)} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={<ChartTooltip formatter={(v) => `${fmtInt(v)} units`} />}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Legend verticalAlign="bottom" iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>
        </SimpleGrid>

        {/* ============================ SECTION 2 — COMMERCIAL PATHWAYS ========================= */}
        <SectionHeading
          title="2 · Where it goes — commercial pathways"
          subtitle="For non-managed material, resellers, repair stations and specialty channels are the intended outlets — not a leak. The question is whether we're placing it, not who we're placing it with."
        />

        <ChartCard
          title="Unmanaged revenue by channel"
          caption="All of these are legitimate outlets for non-managed material. AR core sold to repair stations and resellers runs high margin; SV/OH/New material still reaches end users. Idle unmanaged stock is the real miss, not the channel."
          height={260}
        >
          <QueryState isLoading={channel.isLoading} error={channel.error} isEmpty={!byChannel.length}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byChannel} layout="vertical" margin={{ top: 8, right: 16, left: 40, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.grid} horizontal={false} />
                <XAxis type="number" stroke={c.axis} fontSize={12} tickFormatter={fmtMoney} />
                <YAxis type="category" dataKey="channel" stroke={c.axis} fontSize={12} width={120} />
                <Tooltip
                  content={<ChartTooltip formatter={fmtMoney} />}
                  cursor={{ fill: c.grid, opacity: 0.3 }}
                  wrapperStyle={{ outline: 'none' }}
                />
                <Bar dataKey="revenue" name="Unmanaged revenue" radius={[0, 4, 4, 0]}>
                  {byChannel.map((d) => (
                    <Cell key={d.channel} fill={channelColor(d.channel)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </QueryState>
        </ChartCard>

        {/* ============================ SECTION 3 — PROMOTION CANDIDATES ======================== */}
        <SectionHeading
          title="3 · Promotion candidates"
          subtitle="Unmanaged parts with recurring demand — sold across several months and customers, not one-off deals. These are the parts we keep selling without managing; the case for bringing them onto the managed list."
        />

        <Card withBorder radius="md" p={0}>
          <QueryState isLoading={candidatesQ.isLoading} error={candidatesQ.error} isEmpty={!candidates.length}>
            <ScrollArea.Autosize mah={420}>
              <Table striped highlightOnHover stickyHeader verticalSpacing="xs" fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Part number</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th ta="center">Class</Table.Th>
                    <Table.Th ta="right">Months sold</Table.Th>
                    <Table.Th ta="right">Customers</Table.Th>
                    <Table.Th ta="right">Revenue</Table.Th>
                    <Table.Th ta="right">Margin</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {candidates.map((r) => (
                    <Table.Tr key={r.part}>
                      <Table.Td>
                        <Text ff="monospace" size="xs">
                          {r.part}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {r.desc}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="center">
                        <Badge size="sm" variant="light" color="gray">
                          {r.cls}
                        </Badge>
                      </Table.Td>
                      <Table.Td ta="right">{fmtInt(r.months)}</Table.Td>
                      <Table.Td ta="right">{fmtInt(r.customers)}</Table.Td>
                      <Table.Td ta="right">{fmtMoney(r.revenue)}</Table.Td>
                      <Table.Td ta="right">{fmtPct(r.marginPct)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea.Autosize>
          </QueryState>
        </Card>
        <Text size="xs" c="dimmed">
          Recurrence, not revenue, is the promotion signal — ranked by revenue but filtered to parts sold in 3+
          distinct months, so one-off deals don&apos;t float to the top. Promotion itself stays a human call
          (flip the flag in Pentagon); this just surfaces the strongest cases.
        </Text>
      </Stack>
    </KpiPage>
  );
}
